/**
 * Canonical, versioned ledger / state schemas (R5).
 *
 * These are the authoritative structures every component — the deterministic
 * tools, the turn orchestrator, the frontend, and the eval harness — reads and
 * writes (R5). They are hand-written TypeScript types (mirroring the illustrative
 * JSON in the design's "Data Model (Canonical Schemas)" section) paired with the
 * runtime validators in {@link ./validators.ts}.
 *
 * Every persisted document carries `schemaVersion` (R3.5) via {@link Versioned}
 * so the shape can migrate.
 *
 * The four canonical schemas defined here:
 *   - {@link CharacterLedger}  — R5.1 (identity, stats, resources, inventory,
 *     portable `knowledge`, ownership fields R29.1)
 *   - {@link EncounterState}   — R5.4 (initiative order, current actor, round,
 *     coarse range bands R6.5)
 *   - {@link CampaignState}    — R5.5 (rolling summary, world clock, quests,
 *     `seqToken` R18.5, `advancementMode` R12.1, `paused` flag R21.6)
 *   - {@link SecretLedger}     — R5.6 (DM-only campaign bible + `characterSecrets`,
 *     the campaign-held side of the knowledge/secrets split R9.2)
 *
 * The lightweight `npc` subset validation (R5.2) is layered on in task 4.2; the
 * types here are shaped so the npc subset is a partial of {@link CharacterLedger}.
 */
/** Runtime-checkable set of the SRD condition names (R6.2). */
export const CONDITION_NAMES = [
    "blinded",
    "charmed",
    "deafened",
    "frightened",
    "grappled",
    "incapacitated",
    "invisible",
    "paralyzed",
    "petrified",
    "poisoned",
    "prone",
    "restrained",
    "stunned",
    "unconscious",
    "exhaustion",
];
/** Runtime-checkable set of the SRD damage-type names (R7). */
export const DAMAGE_TYPES = [
    "acid",
    "bludgeoning",
    "cold",
    "fire",
    "force",
    "lightning",
    "necrotic",
    "piercing",
    "poison",
    "psychic",
    "radiant",
    "slashing",
    "thunder",
];
/** Earth-like calendar defaults (60 min/h, 24 h/day, 30 day/month, 12 month/year). */
export const EARTH_LIKE_DEFAULT_CALENDAR = {
    minutesPerHour: 60,
    hoursPerDay: 24,
    daysPerMonth: 30,
    monthsPerYear: 12,
};
/** The closed WeatherKind vocabulary, for validation + enum guards. */
export const WEATHER_KINDS = [
    "clear", "cloudy", "rain", "storm", "fog", "snow", "wind",
];
/** The reserved region id for weather that applies to the whole campaign. */
export const CAMPAIGN_WIDE_REGION = "campaign-wide";
/** The outstanding (not-yet-resolved) statuses — the triage "to-do" set. */
export const OUTSTANDING_ISSUE_STATUSES = [
    "open",
    "investigating",
    "partially-resolved",
];
//# sourceMappingURL=schemas.js.map